'use client';

import { faArrowLeft, faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TeamLeaguesMatchupCard from '@pickleballinc/ui/components/global/Header/TeamLeaguesMatchupCard';
import { useEffect, useRef, useState } from 'react';

import type { Matchup } from '@/modules/ticker/types';

interface TLTickerScrollerProps {
  results: Matchup[];
}

export const TLTickerScroller = ({ results = [] }: TLTickerScrollerProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const lastCardRef = useRef<HTMLDivElement>(null);

  const [cardWidth, setCardWidth] = useState(300);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const [noOfMatchesInSinglePodMatchup, setNoOfMatchesInSinglePodMatchup] = useState(5);
  const [totalNumberOfPodsInMatchup, setTotalNumberOfPodsInMatchup] = useState(1);

  useEffect(() => {
    let maxMatches = 1;
    let maxPods = 1;

    results.forEach((result) => {
      const matchesCount = result.teamOne.podMatches[0]?.matches?.length || 1;
      const podCount = result.teamOne.podMatches.length;

      if (matchesCount > maxMatches) maxMatches = matchesCount;
      if (podCount > maxPods) maxPods = podCount;
    });

    setNoOfMatchesInSinglePodMatchup(maxMatches);
    setTotalNumberOfPodsInMatchup(maxPods);
  }, [results]);

  useEffect(() => {
    if (cardRef.current) {
      const width = cardRef.current.offsetWidth;
      if (width > 0) setCardWidth(width);
    }
  }, [results]);

  const numberOfColumns =
    totalNumberOfPodsInMatchup > 1 ? totalNumberOfPodsInMatchup : noOfMatchesInSinglePodMatchup;

  const updateScrollState = () => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const scrollLeft = container.scrollLeft;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    setCanScrollLeft(scrollLeft > 0);

    if (lastCardRef.current) {
      const containerRight = container.getBoundingClientRect().right;
      const lastCardRight = lastCardRef.current.getBoundingClientRect().right;
      setCanScrollRight(lastCardRight > containerRight + 1); // +1 = tiny buffer
    } else {
      setCanScrollRight(scrollLeft < maxScrollLeft);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      left: direction === 'left' ? -cardWidth : cardWidth,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    updateScrollState();
    const container = scrollRef.current;
    if (!container) return;

    const handle = () => updateScrollState();
    container.addEventListener('scroll', handle);
    window.addEventListener('resize', handle);

    // ResizeObserver re-runs measurement whenever the container or any inner
    // card reflows (fonts loading, images decoding, late layout passes). Fixes
    // a race where the initial one-shot measurement captured a pre-settled
    // layout and left the right arrow hidden until an unrelated RTE update
    // forced a re-render (~1 min on slow loads).
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handle) : null;
    if (observer) {
      observer.observe(container);
      Array.from(container.children).forEach((child) => observer.observe(child));
    }

    return () => {
      container.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
      observer?.disconnect();
    };
  }, [results.length, numberOfColumns]);

  return (
    <div className='relative block h-[148px] w-full overflow-hidden'>
      {canScrollLeft ? (
        <div className='absolute inset-y-0 left-0 z-30 hidden w-24 items-center justify-start bg-gradient-to-r from-white sm:flex'>
          <button
            aria-hidden='true'
            onClick={() => scroll('left')}
            className='group inline-flex cursor-pointer items-center justify-center rounded-e-full border-y border-r border-gray-200 bg-white transition hover:border-none hover:bg-experiment/70 sm:h-16 sm:w-6'
          >
            <FontAwesomeIcon
              icon={faArrowLeft}
              className='stroke-3 text-gray-700 group-hover:text-white'
            />
          </button>
        </div>
      ) : null}

      <div
        ref={scrollRef}
        className='no-scrollbar flex h-full overflow-x-auto scroll-smooth px-2 items-center space-x-2'
      >
        {results.map((matchup, i) => (
          <div
            key={matchup.uuid}
            ref={i === 0 ? cardRef : i === results.length - 1 ? lastCardRef : null}
            className='shrink-0'
          >
            <TeamLeaguesMatchupCard
              matchup={matchup}
              links={matchup.links}
              noOfColumns={numberOfColumns}
              reverseTeamRows
            />
          </div>
        ))}
      </div>

      {canScrollRight ? (
        <div className='absolute inset-y-0 right-0 z-30 hidden w-24 items-center justify-end border-r border-gray-200 bg-gradient-to-l from-white sm:flex'>
          <button
            aria-hidden='true'
            onClick={() => scroll('right')}
            className={`group inline-flex cursor-pointer items-center justify-center rounded-s-full border-y border-s border-gray-200 bg-white transition hover:border-none hover:bg-experiment/70 sm:h-16 sm:w-6`}
          >
            <FontAwesomeIcon
              icon={faArrowRight}
              className='stroke-3 text-gray-700 group-hover:text-white'
            />
          </button>
        </div>
      ) : null}
    </div>
  );
};
